(function () {
  const PRODUCT_CATEGORIES = [
    { id: 'ornaments', keywords: ['ornament'] },
    {
      id: 'flat',
      keywords: [
        'flat',
        'mouse pad',
        'mousepad',
        'coaster',
        'placemat',
        'tile',
        'magnet',
        'sticker',
        'vinyl',
      ],
    },
    {
      id: 'glassware',
      keywords: [
        'glass',
        'pint',
        'mug',
        'tumbler',
        'stemless',
        'wine',
        'rocks',
        'sipper',
        'coozie',
        'koozie',
        'candle',
        'chug',
        'acrylic cup',
      ],
    },
    {
      id: 'water-bottles',
      keywords: [
        'water bottle',
        'waterbottle',
        'bottle',
        'jug',
        'hydrapeak',
        'food jar',
        'sportboot',
        'flask',
        'thermos',
        'drinkware',
      ],
    },
    {
      id: 'apparel',
      keywords: ['shirt', 'tee', 'hoodie', 'sweatshirt', 'apparel', 'cap', 'hat'],
    },
    {
      id: 'bags',
      keywords: ['bag', 'tote', 'backpack', 'lunch box', 'lunchbox', 'bentgo'],
    },
  ];

  function escapeRegex(value) {
    return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }

  function matchesCategoryKeyword(text, keyword) {
    if (keyword.includes(' ')) return text.includes(keyword);
    return new RegExp('\\b' + escapeRegex(keyword) + '\\b', 'i').test(text);
  }

  function getProductCategory(text) {
    const normalized = (text || '').toLowerCase();
    if (!normalized) return 'other';

    for (const category of PRODUCT_CATEGORIES) {
      if (category.keywords.some((keyword) => matchesCategoryKeyword(normalized, keyword))) {
        return category.id;
      }
    }

    return 'other';
  }

  function debounce(fn, wait) {
    let timer;
    return function debounced(...args) {
      clearTimeout(timer);
      timer = setTimeout(() => fn.apply(this, args), wait);
    };
  }

  class PortalCatalog {
    constructor(root) {
      this.root = root;
      this.toolbar = root.querySelector('[data-js-portal-catalog-toolbar]');
      this.cards = () => Array.from(root.querySelectorAll('[data-js-portal-product-card]'));
      this.searchInput = root.querySelector('[data-js-portal-search]');
      this.categoryPills = Array.from(root.querySelectorAll('[data-js-portal-category-pill]'));
      this.emptyMessage = root.querySelector('[data-js-portal-empty-message]');
      this.activeCategory = 'all';
      this.searchTerm = '';
      this.designerOnly = root.getAttribute('data-portal-designer-only') === 'true';
      this.shopDomain = root.getAttribute('data-shop-domain') || '';
      this.designerAppHandle = root.getAttribute('data-designer-app-handle') || 'designer';
      this.designerProductIds = new Set();
      this.designerCatalogLoaded = !this.designerOnly;
      this.designerCatalogFallback = false;
      this.designerCatalogPromise = null;
      this.designerCatalogProducts = [];

      this.assignCategories();
      this.bindToolbar();
      this.bindVariantThumbs();
      this.bindCustomizeButtons();
      this.applyFilters();
      this.loadDesignerCatalog().finally(() => this.applyFilters());
    }

    buildCustomizeUrl(templateProductId) {
      const params = new URLSearchParams();
      params.set('shop', this.shopDomain);
      params.set('productId', templateProductId);
      try {
        params.set('return_to', window.location.href);
      } catch (error) {
        /* ignore */
      }
      return (
        '/apps/' +
        encodeURIComponent(this.designerAppHandle) +
        '/customize?' +
        params.toString()
      );
    }

    async openCustomizerForCard(card, button) {
      const templateId = card.getAttribute('data-dod-template-product-id');
      if (templateId) {
        window.location.href = this.buildCustomizeUrl(templateId);
        return;
      }

      const productGid = card.getAttribute('data-shopify-product-gid');
      if (!productGid || !this.shopDomain) {
        if (button.href) window.location.href = button.href;
        return;
      }

      const originalLabel = button.textContent;
      button.setAttribute('aria-busy', 'true');
      button.textContent = 'Opening…';

      try {
        let apiUrl =
          '/apps/' +
          encodeURIComponent(this.designerAppHandle) +
          '/api/product-lookup?shopifyProductId=' +
          encodeURIComponent(productGid) +
          '&shop=' +
          encodeURIComponent(this.shopDomain);
        const variantGid = card.getAttribute('data-shopify-variant-gid');
        if (variantGid) {
          apiUrl += '&shopifyVariantId=' + encodeURIComponent(variantGid);
        }

        const response = await fetch(apiUrl);
        const data = await response.json();
        if (data && data.product && data.product.id) {
          card.setAttribute('data-dod-template-product-id', data.product.id);
          window.location.href = this.buildCustomizeUrl(data.product.id);
          return;
        }
      } catch (error) {
        console.warn('[portal-catalog] customize lookup failed', error);
      } finally {
        button.removeAttribute('aria-busy');
        button.textContent = originalLabel;
      }

      if (button.href) window.location.href = button.href;
    }

    bindCustomizeButtons() {
      this.root.addEventListener('click', (event) => {
        const button = event.target.closest('[data-js-portal-customize-button]');
        if (!button) return;
        event.preventDefault();
        event.stopPropagation();
        const card = button.closest('[data-js-portal-product-card]');
        if (!card) return;
        this.openCustomizerForCard(card, button);
      });
    }

    loadDesignerCatalog() {
      if (!this.designerOnly || !this.shopDomain) {
        return Promise.resolve();
      }

      if (this.designerCatalogLoaded) {
        this.applyDesignerStateToCards(this.designerCatalogProducts);
        return Promise.resolve();
      }

      if (this.designerCatalogPromise) {
        return this.designerCatalogPromise;
      }

      this.designerCatalogPromise = fetch(
        '/apps/' +
          encodeURIComponent(this.designerAppHandle) +
          '/api/customizer-catalog?shop=' +
          encodeURIComponent(this.shopDomain)
      )
        .then((response) => {
          if (!response.ok) {
            this.designerCatalogFallback = true;
            return null;
          }
          return response.json();
        })
        .then((data) => {
          if (!data || this.designerCatalogFallback) return;
          this.designerCatalogProducts = Array.isArray(data.products) ? data.products : [];
          this.applyDesignerStateToCards(this.designerCatalogProducts);
        })
        .catch((error) => {
          console.warn('[portal-catalog] customizer catalog lookup failed', error);
          this.designerCatalogFallback = true;
        })
        .finally(() => {
          this.designerCatalogLoaded = true;
        });

      return this.designerCatalogPromise;
    }

    applyDesignerStateToCards(products) {
      if (products.length) {
        this.designerProductIds = new Set(
          products
            .map((entry) => (entry.shopifyProductId ? String(entry.shopifyProductId) : ''))
            .filter(Boolean)
        );
      }

      this.cards().forEach((card) => {
        const productId = card.getAttribute('data-shopify-product-id');
        const state = card.getAttribute('data-dod-designer-enabled');
        const inCatalog = Boolean(
          productId && this.designerProductIds.has(String(productId))
        );

        if (this.designerCatalogFallback) return;

        if (state === 'unknown') {
          card.setAttribute('data-dod-designer-enabled', inCatalog ? 'true' : 'false');
        } else if (inCatalog) {
          card.setAttribute('data-dod-designer-enabled', 'true');
        }

        if (!inCatalog) return;

        const match = products.find(
          (entry) => String(entry.shopifyProductId) === String(productId)
        );
        if (!match) return;

        if (match.templateProductId) {
          card.setAttribute('data-dod-template-product-id', match.templateProductId);
        }

        const customizeButton = card.querySelector('[data-js-portal-customize-button]');
        if (customizeButton && match.customizePath) {
          customizeButton.setAttribute('href', match.customizePath);
          customizeButton.hidden = false;
          return;
        }

        const action = card.querySelector('.portal-product-card__action');
        if (!action || action.querySelector('[data-js-portal-customize-button]')) return;

        const link = document.createElement('a');
        link.href = match.customizePath;
        link.className =
          'portal-product-card__button portal-product-card__button--customize';
        link.setAttribute('data-js-portal-customize-button', '');
        link.textContent = 'Customize';
        const viewButton = action.querySelector(
          '.portal-product-card__button:not(.portal-product-card__button--customize)'
        );
        if (viewButton) {
          action.insertBefore(link, viewButton);
        } else {
          action.appendChild(link);
        }
      });
    }

    cardIsDesignerEnabled(card) {
      if (!this.designerOnly) return true;
      if (this.designerCatalogFallback) return true;

      const state = card.getAttribute('data-dod-designer-enabled');
      if (state === 'true') return true;
      if (state === 'false') return false;

      // Metafield not synced yet — show cards until the app catalog response resolves.
      if (!this.designerCatalogLoaded) return true;

      const productId = card.getAttribute('data-shopify-product-id');
      return Boolean(productId && this.designerProductIds.has(String(productId)));
    }

    assignCategories() {
      this.cards().forEach((card) => {
        const searchText = card.getAttribute('data-search-text') || '';
        card.setAttribute('data-portal-category', getProductCategory(searchText));
      });
    }

    bindToolbar() {
      if (this.searchInput) {
        this.searchInput.addEventListener(
          'input',
          debounce((event) => {
            this.searchTerm = event.target.value.trim().toLowerCase();
            this.applyFilters();
          }, 200)
        );

        this.searchInput.addEventListener('keydown', (event) => {
          if (event.key !== 'Enter') return;
          event.preventDefault();
          const query = this.searchInput.value.trim();
          if (!query) return;
          window.location.href = '/search?q=' + encodeURIComponent(query) + '&type=product';
        });
      }

      this.categoryPills.forEach((pill) => {
        pill.addEventListener('click', () => {
          this.activeCategory = pill.getAttribute('data-category') || 'all';
          this.categoryPills.forEach((candidate) => {
            candidate.classList.toggle('is-active', candidate === pill);
          });
          this.applyFilters();
        });
      });
    }

    bindVariantThumbs() {
      this.root.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-js-portal-variant-thumb]');
        if (!thumb) return;

        event.preventDefault();
        event.stopPropagation();

        const card = thumb.closest('[data-js-portal-product-card]');
        if (!card) return;

        card.querySelectorAll('[data-js-portal-variant-thumb]').forEach((candidate) => {
          candidate.classList.toggle('is-selected', candidate === thumb);
        });

        const imageWrap = card.querySelector('[data-js-portal-image-wrap]');
        const skuEl = card.querySelector('[data-js-portal-sku]');
        const variantEl = card.querySelector('[data-js-portal-variant-label]');
        const imageUrl = thumb.getAttribute('data-variant-image');

        if (skuEl) skuEl.textContent = thumb.getAttribute('data-variant-sku') || '';
        if (variantEl) variantEl.textContent = thumb.getAttribute('data-variant-title') || '';

        const variantGid = thumb.getAttribute('data-variant-gid');
        if (variantGid) {
          card.setAttribute('data-shopify-variant-gid', variantGid);
        }

        if (imageWrap && imageUrl) {
          const img = imageWrap.querySelector('img');
          if (img) {
            img.src = imageUrl;
            img.srcset = imageUrl + ' 360w';
          }
        }
      });
    }

    applyFilters() {
      let visibleCount = 0;

      this.cards().forEach((card) => {
        const category = card.getAttribute('data-portal-category') || 'other';
        const searchText = card.getAttribute('data-search-text') || '';
        const matchesCategory =
          this.activeCategory === 'all' || category === this.activeCategory;
        const matchesSearch = !this.searchTerm || searchText.includes(this.searchTerm);
        const matchesDesigner = this.cardIsDesignerEnabled(card);
        const visible = matchesCategory && matchesSearch && matchesDesigner;

        card.classList.toggle('is-hidden', !visible);
        if (visible) visibleCount += 1;
      });

      if (this.emptyMessage) {
        this.emptyMessage.classList.toggle(
          'portal-catalog-toolbar__empty--hidden',
          visibleCount > 0
        );
      }
    }

    refresh() {
      this.assignCategories();
      this.loadDesignerCatalog().finally(() => this.applyFilters());
    }
  }

  function initPortalCatalog(scope) {
    const root = scope || document;
    root.querySelectorAll('.portal-catalog').forEach((section) => {
      if (section._portalCatalog) {
        section._portalCatalog.refresh();
        return;
      }
      section._portalCatalog = new PortalCatalog(section);
    });
  }

  document.addEventListener('DOMContentLoaded', () => initPortalCatalog());

  document.addEventListener('shopify:section:load', (event) => {
    initPortalCatalog(event.target);
  });

  window.PortalCatalog = { init: initPortalCatalog };
})();
